// src/pages/AdminPersonal.js
// =========================================================================
// 👥 GESTIÓN DE PERSONAL DE LA CLÍNICA (solo admin)
//
// Alta, edición, baja y reactivación de profesionales, y ayuda a quien se ha
// quedado fuera: contraseña temporal y retirada del 2FA. Todo pasa por
// /api/profesionales (auditado en el backend). Reglas que impone el servidor
// y que aquí solo se anticipan:
//   - médico y supermédico necesitan nº de colegiado; recepción y admin, no
//   - nadie puede darse de baja ni cambiarse el rol a sí mismo
//   - siempre debe quedar al menos un administrador activo
// =========================================================================
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { ESPECIALIDADES } from '../utils/formatos';

const ROLES = [
    { valor: 'medico', etiqueta: 'Médico', ayuda: 'Atiende consultas y ve los pacientes con los que tiene relación clínica.' },
    { valor: 'supermedico', etiqueta: 'Supermédico', ayuda: 'Médico con visión de toda la clínica (dirección médica).' },
    { valor: 'recepcion', etiqueta: 'Recepción', ayuda: 'Agenda, pacientes y facturación. Sin acceso a la historia clínica.' },
    { valor: 'admin', etiqueta: 'Administración', ayuda: 'Gestiona personal, datos fiscales y auditoría.' }
];
const ETIQUETA_ROL = Object.fromEntries(ROLES.map(r => [r.valor, r.etiqueta]));
const COLOR_ROL = {
    medico: 'bg-sky-50 text-sky-700 border-sky-200',
    supermedico: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    recepcion: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    admin: 'bg-amber-50 text-amber-700 border-amber-200'
};
const esSanitario = (rol) => rol === 'medico' || rol === 'supermedico';

const inputBase = 'w-full p-3 bg-slate-50 border rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 transition-colors disabled:opacity-60';
const inputOk = 'border-slate-200 focus:border-clinica-primary focus:ring-clinica-primary/15';
const inputError = 'border-rose-300 focus:border-rose-500 focus:ring-rose-500/15';

// El backend responde 'Errores de validación: campo : mensaje | …'
const limpiarError = (err, porDefecto) => String(err?.message || porDefecto)
    .replace(/^Errores de validación:\s*/i, '')
    .replace(/(^|\|\s*)[\w.]+\s:\s/g, '$1');

function Campo({ id, label, error, ayuda, children }) {
    return (
        <div>
            <label htmlFor={id} className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">{label}</label>
            {children}
            {error
                ? <p className="mt-1.5 text-xs font-semibold text-rose-600">{error}</p>
                : ayuda && <p className="mt-1.5 text-xs font-medium text-slate-400">{ayuda}</p>}
        </div>
    );
}

function Modal({ titulo, onCerrar, children, ancho = 'max-w-2xl' }) {
    useEffect(() => {
        const alPulsar = (e) => { if (e.key === 'Escape') onCerrar(); };
        window.addEventListener('keydown', alPulsar);
        return () => window.removeEventListener('keydown', alPulsar);
    }, [onCerrar]);

    return (
        // Clic en el fondo (no dentro del diálogo) → cerrar; Escape lo gestiona el efecto de arriba
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
            role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onCerrar(); }}>
            <div role="dialog" aria-modal="true" aria-label={titulo}
                className={`w-full ${ancho} max-h-[92vh] overflow-y-auto bg-white rounded-[2rem] shadow-2xl border border-slate-200`}>
                <div className="flex items-center justify-between px-6 md:px-8 pt-6 pb-4 border-b border-slate-100">
                    <h2 className="text-xl font-black text-slate-900">{titulo}</h2>
                    <button type="button" onClick={onCerrar} aria-label="Cerrar"
                        className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                <div className="px-6 md:px-8 py-6">{children}</div>
            </div>
        </div>
    );
}

// -------------------------------------------------------------------------
// Formulario de edición (las altas se hacen desde el servidor: scripts/personal.js)
// -------------------------------------------------------------------------
function FormularioProfesional({ inicial, onGuardado, onCancelar }) {
    const [form, setForm] = useState(() => ({
        nombre: inicial?.nombre || '',
        apellidos: inicial?.apellidos || '',
        email: inicial?.email || '',
        telefono: inicial?.telefono || '',
        role: inicial?.role || 'medico',
        especialidad: inicial?.especialidad || 'medicina general',
        colegiado: inicial?.colegiado || '',
        precioConsulta: inicial?.precioConsulta ?? 50
    }));
    const [errores, setErrores] = useState({});
    const [errorServidor, setErrorServidor] = useState('');
    const [guardando, setGuardando] = useState(false);
    const refNombre = useRef(null);

    // Foco en el primer campo al abrir el formulario
    useEffect(() => { refNombre.current?.focus(); }, []);

    const set = (campo, valor) => {
        setForm(f => ({ ...f, [campo]: valor }));
        if (errores[campo]) setErrores(e => ({ ...e, [campo]: undefined }));
        if (errorServidor) setErrorServidor('');
    };

    const validar = () => {
        const e = {};
        if (form.nombre.trim().length < 2) e.nombre = 'Indica el nombre.';
        if (form.apellidos.trim().length < 2) e.apellidos = 'Indica los apellidos.';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Email no válido.';
        if (esSanitario(form.role) && form.colegiado.trim().length < 3) e.colegiado = 'Obligatorio para médicos.';
        if (esSanitario(form.role) && !(Number(form.precioConsulta) >= 0)) e.precioConsulta = 'Indica un precio válido.';
        return e;
    };

    const guardar = async (ev) => {
        ev.preventDefault();
        const encontrados = validar();
        setErrores(encontrados);
        if (Object.keys(encontrados).length) return;

        const payload = {
            nombre: form.nombre.trim(),
            apellidos: form.apellidos.trim(),
            email: form.email.trim().toLowerCase(),
            telefono: form.telefono.trim(),
            // Rol y especialidad no se envían: solo se cambian desde el servidor (scripts/personal.js)
            // Recepción/admin sin colegiado: no se envía y el servidor conserva
            // (o asigna) su identificador interno
            ...((esSanitario(form.role) || form.colegiado.trim()) && { colegiado: form.colegiado.trim() }),
            precioConsulta: esSanitario(form.role) ? Number(form.precioConsulta) : 0
        };

        try {
            setGuardando(true);
            await api.put(`/profesionales/${inicial.id}`, payload);
            onGuardado();
        } catch (err) {
            setErrorServidor(limpiarError(err, 'No se pudo guardar.'));
        } finally {
            setGuardando(false);
        }
    };

    const cls = (campo) => `${inputBase} ${errores[campo] ? inputError : inputOk}`;
    const rolActual = ROLES.find(r => r.valor === form.role);

    return (
        <form onSubmit={guardar} noValidate className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Campo id="p-nombre" label="Nombre" error={errores.nombre}>
                    <input id="p-nombre" value={form.nombre} onChange={e => set('nombre', e.target.value)} className={cls('nombre')} ref={refNombre} />
                </Campo>
                <Campo id="p-apellidos" label="Apellidos" error={errores.apellidos}>
                    <input id="p-apellidos" value={form.apellidos} onChange={e => set('apellidos', e.target.value)} className={cls('apellidos')} />
                </Campo>
                <Campo id="p-email" label="Email (para iniciar sesión)" error={errores.email}>
                    <input id="p-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} className={cls('email')} />
                </Campo>
                <Campo id="p-telefono" label="Teléfono (opcional)">
                    <input id="p-telefono" value={form.telefono} onChange={e => set('telefono', e.target.value)} className={cls('telefono')} />
                </Campo>
            </div>

            <Campo id="p-rol" label="Rol" ayuda={`${rolActual?.ayuda || ''} El rol y la especialidad los cambia el soporte de Klyndu.`.trim()}>
                <input id="p-rol" value={rolActual?.etiqueta || form.role} readOnly disabled className={cls('role')} />
            </Campo>

            {esSanitario(form.role) && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 p-5 rounded-2xl bg-slate-50/70 border border-slate-100">
                    <Campo id="p-esp" label="Especialidad">
                        <input id="p-esp" value={ESPECIALIDADES.find(e => e[0] === form.especialidad)?.[1] || form.especialidad} readOnly disabled className={cls('especialidad')} />
                    </Campo>
                    <Campo id="p-col" label="Nº de colegiado" error={errores.colegiado}>
                        <input id="p-col" value={form.colegiado} onChange={e => set('colegiado', e.target.value)} className={`${cls('colegiado')} font-mono`} />
                    </Campo>
                    <Campo id="p-precio" label="Precio consulta (€)" error={errores.precioConsulta}>
                        <input id="p-precio" type="number" min="0" step="1" value={form.precioConsulta} onChange={e => set('precioConsulta', e.target.value)} className={cls('precioConsulta')} />
                    </Campo>
                </div>
            )}

            {errorServidor && (
                <p role="alert" className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm font-semibold text-rose-700">{errorServidor}</p>
            )}

            <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={onCancelar} className="px-5 py-3 rounded-xl text-sm font-black text-slate-600 hover:bg-slate-100">Cancelar</button>
                <button type="submit" disabled={guardando}
                    className="px-6 py-3 rounded-xl bg-slate-900 text-white text-sm font-black hover:bg-slate-800 disabled:opacity-60">
                    {guardando ? 'Guardando…' : 'Guardar cambios'}
                </button>
            </div>
        </form>
    );
}

// -------------------------------------------------------------------------
// Credenciales para entregar (alta o contraseña restablecida)
// -------------------------------------------------------------------------
function Credenciales({ datos, onCerrar }) {
    const [copiado, setCopiado] = useState(false);
    const texto = `Usuario: ${datos.email}\nContraseña: ${datos.password}`;
    const copiar = async () => {
        try {
            await navigator.clipboard.writeText(texto);
            setCopiado(true);
        } catch {
            setCopiado(false);
        }
    };
    return (
        <Modal titulo={datos.titulo} onCerrar={onCerrar} ancho="max-w-lg">
            <p className="text-sm font-semibold text-slate-600 mb-4">
                Entrégale estos datos a <span className="font-black text-slate-900">{datos.nombre}</span>. La contraseña
                <span className="font-black"> no se volverá a mostrar</span>; le recomendamos cambiarla y activar el 2FA desde su perfil.
            </p>
            <div className="p-4 rounded-2xl bg-slate-900 text-slate-100 font-mono text-sm space-y-1 select-all">
                <div><span className="text-slate-400">Usuario:</span> {datos.email}</div>
                <div><span className="text-slate-400">Contraseña:</span> {datos.password}</div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={copiar} className="px-5 py-3 rounded-xl border border-slate-200 text-sm font-black text-slate-700 hover:bg-slate-50">
                    {copiado ? 'Copiado' : 'Copiar'}
                </button>
                <button type="button" onClick={onCerrar} className="px-6 py-3 rounded-xl bg-slate-900 text-white text-sm font-black hover:bg-slate-800">Hecho</button>
            </div>
        </Modal>
    );
}

// -------------------------------------------------------------------------
// Confirmación de acciones sensibles
// -------------------------------------------------------------------------
function Confirmar({ accion, onConfirmar, onCerrar, procesando, error }) {
    return (
        <Modal titulo={accion.titulo} onCerrar={onCerrar} ancho="max-w-md">
            <p className="text-sm font-semibold text-slate-600">{accion.texto}</p>
            {error && <p role="alert" className="mt-4 p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm font-semibold text-rose-700">{error}</p>}
            <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={onCerrar} className="px-5 py-3 rounded-xl text-sm font-black text-slate-600 hover:bg-slate-100">Cancelar</button>
                <button type="button" onClick={onConfirmar} disabled={procesando}
                    className={`px-6 py-3 rounded-xl text-white text-sm font-black disabled:opacity-60 ${accion.peligro ? 'bg-rose-600 hover:bg-rose-700' : 'bg-slate-900 hover:bg-slate-800'}`}>
                    {procesando ? 'Un momento…' : accion.boton}
                </button>
            </div>
        </Modal>
    );
}

// -------------------------------------------------------------------------
// Página
// -------------------------------------------------------------------------
export default function AdminPersonal() {
    const { showToast } = useToast();
    const toastRef = useRef(showToast);
    toastRef.current = showToast;
    const { user } = useAuth();

    const [personal, setPersonal] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [vista, setVista] = useState('activos');
    const [busqueda, setBusqueda] = useState('');
    const [formulario, setFormulario] = useState(null);   // { inicial } | null
    const [credenciales, setCredenciales] = useState(null);
    const [confirmacion, setConfirmacion] = useState(null);
    const [procesando, setProcesando] = useState(false);
    const [errorAccion, setErrorAccion] = useState('');

    const cargar = useCallback(async () => {
        try {
            setCargando(true);
            const res = await api.get('/profesionales/gestion');
            setPersonal(Array.isArray(res) ? res : []);
        } catch (err) {
            toastRef.current(err.message || 'No se pudo cargar el personal.', 'error');
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const activos = personal.filter(p => p.activo);
    const deBaja = personal.filter(p => !p.activo);

    const visibles = useMemo(() => {
        const base = vista === 'activos' ? activos : deBaja;
        const q = busqueda.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        if (!q) return base;
        return base.filter(p => `${p.nombre} ${p.apellidos} ${p.email} ${ETIQUETA_ROL[p.role] || ''}`
            .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(q));
    }, [vista, activos, deBaja, busqueda]);

    const conteoRoles = useMemo(() => ROLES.map(r => ({ ...r, n: activos.filter(p => p.role === r.valor).length })), [activos]);

    const pedir = (accion) => { setErrorAccion(''); setConfirmacion(accion); };

    const ejecutar = async () => {
        const { tipo, persona } = confirmacion;
        try {
            setProcesando(true);
            if (tipo === 'password') {
                const res = await api.post(`/profesionales/${persona.id}/restablecer-password`);
                setCredenciales({ titulo: 'Contraseña restablecida', nombre: `${persona.nombre} ${persona.apellidos}`, email: persona.email, password: res.passwordTemporal });
            } else if (tipo === '2fa') {
                await api.post(`/profesionales/${persona.id}/restablecer-2fa`);
                showToast(`2FA retirado a ${persona.nombre}. Deberá volver a activarlo desde su perfil.`, 'success');
            }
            setConfirmacion(null);
            cargar();
        } catch (err) {
            setErrorAccion(limpiarError(err, 'No se pudo completar la acción.'));
        } finally {
            setProcesando(false);
        }
    };

    const alGuardar = () => {
        setFormulario(null);
        showToast('Cambios guardados.', 'success');
        cargar();
    };

    return (
        <div className="p-6 md:p-8 space-y-6 max-w-6xl mx-auto animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight">Personal de la clínica</h1>
                    <p className="text-sm font-semibold text-slate-500 mt-1">Quién puede entrar en el programa y con qué permisos.</p>
                </div>
                <p className="text-xs font-semibold text-slate-500 md:max-w-xs md:text-right">
                    Las altas, bajas y reactivaciones de personal las gestiona el soporte de Klyndu. Aquí puedes editar datos, restablecer contraseñas y el 2FA.
                </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {conteoRoles.map(r => (
                    <div key={r.valor} className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{r.etiqueta}</div>
                        <div className="text-2xl font-black text-slate-900 mt-1">{r.n}</div>
                    </div>
                ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-[2rem] shadow-sm overflow-hidden">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 md:p-5 border-b border-slate-100">
                    <div className="inline-flex p-1 bg-slate-100 rounded-xl" role="tablist">
                        {[['activos', `Activos (${activos.length})`], ['baja', `De baja (${deBaja.length})`]].map(([v, t]) => (
                            <button key={v} type="button" role="tab" aria-selected={vista === v} onClick={() => setVista(v)}
                                className={`px-4 py-2 rounded-lg text-xs font-black transition-colors ${vista === v ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                                {t}
                            </button>
                        ))}
                    </div>
                    <input type="search" value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por nombre, email o rol…"
                        aria-label="Buscar personal" className={`${inputBase} ${inputOk} md:max-w-xs`} />
                </div>

                {cargando ? (
                    <div className="p-6 space-y-3">{[0, 1, 2].map(i => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}</div>
                ) : visibles.length === 0 ? (
                    <div className="p-10 text-center text-sm font-semibold text-slate-400">
                        {busqueda ? 'Nadie coincide con la búsqueda.' : vista === 'activos' ? 'No hay personal activo.' : 'No hay nadie dado de baja.'}
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {visibles.map(p => {
                            const soyYo = String(p.id) === String(user?.id);
                            return (
                                <li key={p.id} className="flex flex-col lg:flex-row lg:items-center gap-3 p-4 md:px-5">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <div className="w-10 h-10 shrink-0 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-black">
                                            {(p.nombre?.[0] || '?').toUpperCase()}{(p.apellidos?.[0] || '').toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="font-black text-slate-900 truncate">{p.nombre} {p.apellidos}</span>
                                                {soyYo && <span className="text-[10px] font-black text-slate-400 uppercase">(tú)</span>}
                                                <span className={`px-2 py-0.5 rounded-md border text-[10px] font-black uppercase tracking-wide ${COLOR_ROL[p.role] || ''}`}>{ETIQUETA_ROL[p.role] || p.role}</span>
                                                {p.isTwoFactorEnabled && <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-black uppercase">2FA</span>}
                                                {p.bloqueado && <span className="px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-black uppercase">Bloqueado</span>}
                                            </div>
                                            <div className="text-xs font-semibold text-slate-500 truncate">
                                                {p.email}
                                                {esSanitario(p.role) && <> · {ESPECIALIDADES.find(e => e[0] === p.especialidad)?.[1] || p.especialidad}{p.colegiado && <> · Col. {p.colegiado}</>}</>}
                                                {p.telefono && <> · {p.telefono}</>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2 lg:justify-end">
                                        {p.activo && (
                                            <>
                                                <button type="button" onClick={() => setFormulario({ inicial: p })}
                                                    className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-black text-slate-700 hover:bg-slate-50">Editar</button>
                                                <button type="button"
                                                    onClick={() => pedir({ tipo: 'password', persona: p, titulo: 'Restablecer contraseña', boton: 'Generar contraseña temporal', texto: `Se creará una contraseña temporal para ${p.nombre} ${p.apellidos} y se cerrarán sus sesiones abiertas. También se le quita el bloqueo por intentos fallidos.` })}
                                                    className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-black text-slate-700 hover:bg-slate-50">Contraseña</button>
                                                {p.isTwoFactorEnabled && (
                                                    <button type="button"
                                                        onClick={() => pedir({ tipo: '2fa', persona: p, titulo: 'Quitar el 2FA', boton: 'Quitar 2FA', texto: `Úsalo si ${p.nombre} ha perdido el móvil. Podrá entrar solo con su contraseña hasta que vuelva a activar el 2FA desde su perfil.` })}
                                                        className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-black text-slate-700 hover:bg-slate-50">Quitar 2FA</button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {formulario && (
                <Modal titulo={`Editar a ${formulario.inicial.nombre}`} onCerrar={() => setFormulario(null)}>
                    <FormularioProfesional
                        inicial={formulario.inicial}
                        onGuardado={alGuardar}
                        onCancelar={() => setFormulario(null)} />
                </Modal>
            )}

            {confirmacion && (
                <Confirmar accion={confirmacion} onConfirmar={ejecutar} onCerrar={() => setConfirmacion(null)} procesando={procesando} error={errorAccion} />
            )}

            {credenciales && <Credenciales datos={credenciales} onCerrar={() => setCredenciales(null)} />}
        </div>
    );
}
